import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, AlertCircle, Download, Upload, Search, X, Building2, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTickets, useBranches, useTeamMembers, useCustomers } from '../hooks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const TicketList = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [branchFilter, setBranchFilter] = useState('all');
  const [createdByFilter, setCreatedByFilter] = useState('all');
  const [assignedToFilter, setAssignedToFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const fileInputRef = useRef(null);
  const customerInputRef = useRef(null);
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Use custom hooks for data fetching
  const { tickets, loading, refetch: refetchTickets } = useTickets();
  const { branches } = useBranches();
  const { teamMembers, getFilteredByBranch } = useTeamMembers();
  const { customers, searchCustomers } = useCustomers();

  // Initialize filters from URL params
  useEffect(() => {
    const assignedTo = searchParams.get('assignedTo');
    const status = searchParams.get('status');
    
    if (assignedTo) {
      setAssignedToFilter(assignedTo);
    }
    if (status) {
      setStatusFilter(status);
    }
  }, [searchParams]);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    product_name: '',
    service_center_complaint_number: '',
    warranty_status: 'under_warranty',
    amount: '',
    remark: '',
    priority: 'medium'
  });

  useEffect(() => {
    filterTickets();
  }, [searchQuery, branchFilter, createdByFilter, assignedToFilter, statusFilter, tickets]);

  const filterTickets = () => {
    let filtered = [...tickets];
    
    // Apply branch filter
    if (branchFilter && branchFilter !== 'all') {
      filtered = filtered.filter(ticket => ticket.branch_id === branchFilter);
    }
    
    // Apply created by filter
    if (createdByFilter && createdByFilter !== 'all') {
      filtered = filtered.filter(ticket => ticket.created_by === createdByFilter);
    }
    
    // Apply assigned to filter
    if (assignedToFilter && assignedToFilter !== 'all') {
      if (assignedToFilter === 'unassigned') {
        filtered = filtered.filter(ticket => !ticket.assigned_to);
      } else {
        filtered = filtered.filter(ticket => ticket.assigned_to === assignedToFilter);
      }
    }
    
    // Apply status filter
    if (statusFilter && statusFilter !== 'all') {
      filtered = filtered.filter(ticket => ticket.status === statusFilter);
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(ticket => 
        ticket.customer_name?.toLowerCase().includes(query) ||
        ticket.service_center_complaint_number?.toLowerCase().includes(query) ||
        ticket.product_name?.toLowerCase().includes(query) ||
        customers.find(c => c.id === ticket.customer_id)?.phone?.toLowerCase().includes(query)
      );
    }
    
    setFilteredTickets(filtered);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setBranchFilter('all');
    setCreatedByFilter('all');
    setAssignedToFilter('all');
    setStatusFilter('all');
    setSearchParams({});
  };

  const hasActiveFilters = searchQuery || branchFilter !== 'all' || createdByFilter !== 'all' || assignedToFilter !== 'all' || statusFilter !== 'all';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (submitting) return; // Prevent double submission
    
    setSubmitting(true);
    try {
      const submitData = {
        customer_id: formData.customer_id,
        product_name: formData.product_name,
        service_center_complaint_number: formData.service_center_complaint_number || null,
        warranty_status: formData.warranty_status,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        remark: formData.remark || null,
        priority: formData.priority
      };
      await axios.post(`${API}/tickets`, submitData);
      toast.success('Ticket created successfully');
      setDialogOpen(false);
      setFormData({
        customer_id: '',
        customer_name: '',
        product_name: '',
        service_center_complaint_number: '',
        warranty_status: 'under_warranty',
        amount: '',
        remark: '',
        priority: 'medium'
      });
      setCustomerSearch('');
      refetchTickets();
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-orange-100 text-orange-900 border-2 border-orange-400',
      pending: 'bg-red-100 text-red-900 border-2 border-red-400',
      in_progress: 'bg-red-100 text-red-900 border-2 border-red-400',
      closed: 'bg-green-100 text-green-900 border-2 border-green-400',
      resolved: 'bg-green-100 text-green-900 border-2 border-green-400',
    };
    return colors[status] || 'bg-slate-100 text-slate-900';
  };

  const getDisplayStatus = (status) => {
    const statusMap = {
      open: 'Open',
      pending: 'Pending',
      in_progress: 'Pending',
      closed: 'Closed',
      resolved: 'Closed',
    };
    return statusMap[status] || status;
  };

  const getWarrantyColor = (warranty) => {
    const colors = {
      under_warranty: 'bg-green-100 text-green-900',
      out_of_warranty: 'bg-red-100 text-red-900',
      extended_warranty: 'bg-blue-100 text-blue-900',
    };
    return colors[warranty] || 'bg-slate-100 text-slate-900';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/tickets/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tickets_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Tickets exported successfully');
    } catch (error) {
      toast.error('Failed to export tickets');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImporting(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(`${API}/tickets/import`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      setImportDialogOpen(false);
      refetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import tickets');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  // Use hook's search function for customers
  const filteredCustomers = searchCustomers(customerSearch);

  // Use hook's filter function for team members by branch
  const filteredTeamMembers = getFilteredByBranch(branchFilter);

  // Reset created by filter when branch changes and selected member is not in the new branch
  useEffect(() => {
    if (createdByFilter !== 'all' && branchFilter !== 'all') {
      const memberInBranch = teamMembers.find(m => m.id === createdByFilter && (m.branch_id === branchFilter || m.role === 'admin'));
      if (!memberInBranch) {
        setCreatedByFilter('all');
      }
    }
  }, [branchFilter, createdByFilter, teamMembers]);

  const handleCustomerSelect = (customer) => {
    setFormData({ ...formData, customer_id: customer.id, customer_name: customer.name });
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
  };

  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearch(value);
    setShowCustomerDropdown(value.length >= 3);
    if (value.length < 3) {
      setFormData({ ...formData, customer_id: '', customer_name: '' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      <Navbar setSidebarOpen={setSidebarOpen} />
      
      <main className="md:ml-64 pt-16">
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2" data-testid="tickets-title">Support Tickets</h1>
              <p className="text-slate-600">Manage customer support requests</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleExport} data-testid="export-tickets-button">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="import-tickets-button">
                    <Upload className="h-4 w-4 mr-2" />
                    Import
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Import Tickets from Excel</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                      Upload an Excel file (.xlsx) with columns: Customer (name or email), Product Name, Complaint Number, Warranty Status, Amount, Status, Priority, Remark
                    </p>
                    <Input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleImport} disabled={importing} data-testid="import-tickets-file" />
                    {importing && <p className="text-sm text-slate-600">Importing...</p>}
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) {
                  setCustomerSearch('');
                  setShowCustomerDropdown(false);
                }
              }}>
                <DialogTrigger asChild>
                  <Button data-testid="create-ticket-button">
                    <Plus className="h-4 w-4 mr-2" />
                    New Ticket
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create New Ticket</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Customer Search Input */}
                    <div className="space-y-2">
                      <Label htmlFor="customer_search">Customer Name *</Label>
                      <p className="text-xs text-slate-500">Type at least 3 characters to search</p>
                      <div className="relative">
                        <Input
                          ref={customerInputRef}
                          id="customer_search"
                          value={customerSearch}
                          onChange={handleCustomerSearchChange}
                          onFocus={() => customerSearch.length >= 3 && setShowCustomerDropdown(true)}
                          placeholder="Type customer name or phone..."
                          autoComplete="off"
                          data-testid="ticket-customer-search"
                        />
                        {showCustomerDropdown && filteredCustomers.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                            {filteredCustomers.map((customer) => (
                              <div
                                key={customer.id}
                                className="px-4 py-2 hover:bg-slate-100 cursor-pointer border-b border-slate-100 last:border-b-0"
                                onClick={() => handleCustomerSelect(customer)}
                                data-testid={`customer-option-${customer.id}`}
                              >
                                <p className="font-medium text-slate-900">{customer.name}</p>
                                <p className="text-xs text-slate-500">{customer.phone} • {customer.email}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        {showCustomerDropdown && customerSearch.length >= 3 && filteredCustomers.length === 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg p-4 text-center text-slate-500">
                            No customers found
                          </div>
                        )}
                      </div>
                      {formData.customer_id && (
                        <p className="text-xs text-green-600">✓ Customer selected: {formData.customer_name}</p>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="product_name">Product Name *</Label>
                      <Input
                        id="product_name"
                        value={formData.product_name}
                        onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                        required
                        placeholder="e.g., Samsung TV 55 inch"
                        data-testid="ticket-product-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service_center_complaint_number">Service Center Complaint Number *</Label>
                      <Input
                        id="service_center_complaint_number"
                        value={formData.service_center_complaint_number}
                        onChange={(e) => setFormData({ ...formData, service_center_complaint_number: e.target.value })}
                        required
                        placeholder="e.g., SC-2024-001234"
                        data-testid="ticket-complaint-number-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="warranty_status">Warranty Status *</Label>
                        <Select value={formData.warranty_status} onValueChange={(value) => setFormData({ ...formData, warranty_status: value })}>
                          <SelectTrigger data-testid="ticket-warranty-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="under_warranty">Under Warranty</SelectItem>
                            <SelectItem value="out_of_warranty">Out of Warranty</SelectItem>
                            <SelectItem value="extended_warranty">Extended Warranty</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="priority">Priority *</Label>
                        <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                          <SelectTrigger data-testid="ticket-priority-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="amount">Amount (₹)</Label>
                      <Input
                        id="amount"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        placeholder="e.g., 12000"
                        data-testid="ticket-amount-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="remark">Remark</Label>
                      <Textarea
                        id="remark"
                        value={formData.remark}
                        onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
                        rows={2}
                        placeholder="Any additional notes..."
                        data-testid="ticket-remark-input"
                      />
                    </div>
                    <div className="pt-4 border-t border-slate-200">
                      <Button type="submit" className="w-full" size="lg" disabled={submitting} data-testid="submit-ticket-button">
                        {submitting ? 'Saving...' : 'Save Now'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Search Bar and Filters */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search by customer name, phone, or complaint number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10"
                  data-testid="ticket-search-input"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              {/* Branch Filter */}
              {branches.length > 0 && (
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-slate-500" />
                  <Select value={branchFilter} onValueChange={setBranchFilter}>
                    <SelectTrigger className="w-40" data-testid="ticket-branch-filter">
                      <SelectValue placeholder="Filter by Branch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Branches</SelectItem>
                      {branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36" data-testid="ticket-status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Assigned To Filter */}
              {filteredTeamMembers.length > 0 && (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-slate-500" />
                  <Select value={assignedToFilter} onValueChange={setAssignedToFilter}>
                    <SelectTrigger className="w-40" data-testid="ticket-assigned-to-filter">
                      <SelectValue placeholder="Assigned To" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Assignees</SelectItem>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {filteredTeamMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Created By Filter */}
              {filteredTeamMembers.length > 0 && (
                <div className="flex items-center gap-2">
                  <Select value={createdByFilter} onValueChange={setCreatedByFilter}>
                    <SelectTrigger className="w-40" data-testid="ticket-created-by-filter">
                      <SelectValue placeholder="Created By" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Creators</SelectItem>
                      {filteredTeamMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            {hasActiveFilters && (
              <div className="flex items-center gap-4 mt-2">
                <p className="text-sm text-slate-500">
                  Found {filteredTickets.length} ticket{filteredTickets.length !== 1 ? 's' : ''}
                </p>
                <button
                  onClick={clearFilters}
                  className="text-sm text-accent hover:underline"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>

          {/* Tickets List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600">
                {hasActiveFilters ? 'No tickets match your search/filter criteria.' : 'No tickets yet. Create your first ticket to get started.'}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Product</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Complaint #</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Warranty</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Assigned To</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Created By</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {filteredTickets.map((ticket) => (
                      <tr key={ticket.id} className="hover:bg-slate-50 transition-colors" data-testid={`ticket-row-${ticket.id}`}>
                        <td className="px-6 py-4">
                          <Link to={`/tickets/${ticket.id}`} className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                            {ticket.product_name}
                          </Link>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{ticket.customer_name || 'Unknown'}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{ticket.service_center_complaint_number}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${getWarrantyColor(ticket.warranty_status)}`}>
                            {ticket.warranty_status?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase ${getStatusColor(ticket.status)}`}>
                            {getDisplayStatus(ticket.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{ticket.assigned_to_name || 'Unassigned'}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{ticket.created_by_name || '-'}</td>
                        <td className="px-6 py-4 text-sm text-slate-700">{formatDate(ticket.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
